import { Team } from "@redotech/redo-model/team";
import { groupInput, input, InputProvider } from "@redotech/ui/form";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import round from "lodash/round";
import {
  minMaxValidator,
  pricingDefault,
  pricingFormValues,
  pricingRulesConvertTeamToValue,
  pricingRulesConvertValueToTeam,
} from "./dynamic-pricing";

export const finalSaleReturnsForm = groupInput(
  {
    enabled: input<boolean>(),
    /**
     * @deprecated Use validCollectionTitles instead. All of the other collection models use the title,
     * not the handle, so we need to store both. After the migration, we can remove this field.
     */
    validCollections: input<string[]>({ equal: arrayEqual(stringEqual) }),
    validCollectionTitles: input<string[]>({ equal: arrayEqual(stringEqual) }),
    validProductTags: input<string[]>({ equal: arrayEqual(stringEqual) }),
    reimbursementPercentage: input<string>(),
    shippingLabelCoveragePrice: input<string>(),
    finalSaleRevSharePercentage: input<string>(),
    allRedoPurchasesAreFinalSale: input<boolean>(),
    allCovered: input<boolean>(),
    allEligible: input<boolean>(),
    ...pricingFormValues,
  },
  { validator: minMaxValidator },
);

export type FinalSaleReturnsForm = InputProvider.Form<
  typeof finalSaleReturnsForm
>;

export type FinalSaleReturnsValue = InputProvider.Value<
  typeof finalSaleReturnsForm
>;

export function finalSaleReturnsTeamToFormValue(
  team: Team,
): FinalSaleReturnsValue {
  return {
    // General
    enabled: team.settings.returns?.finalSaleReturns?.enabled || false,
    validCollections:
      team.settings.returns?.finalSaleReturns?.validCollections || [],
    validCollectionTitles:
      team.settings.returns?.finalSaleReturns?.validCollectionTitles || [],
    validProductTags:
      team.settings.returns?.finalSaleReturns?.validProductTags || [],
    minPrice:
      team.settings.returns?.finalSaleReturns?.minPrice?.toString() || "1",
    maxPrice:
      team.settings.returns?.finalSaleReturns?.maxPrice?.toString() || "15",
    percentage: team.settings.returns?.finalSaleReturns?.percentage
      ? String(
          round(team.settings.returns.finalSaleReturns.percentage * 100.0, 3),
        )
      : "2",
    reimbursementPercentage: team.settings.returns?.finalSaleReturns
      ?.reimbursementPercentage
      ? String(
          round(
            team.settings.returns.finalSaleReturns.reimbursementPercentage *
              100.0,
            3,
          ),
        )
      : "100",
    // Use pricePerOrder for returns coverage as the default
    shippingLabelCoveragePrice:
      team.settings.returns?.finalSaleReturns?.shippingLabelCoveragePrice?.toString() ||
      team.pricePerOrder ||
      "",
    finalSaleRevSharePercentage: team.settings.returns?.finalSaleReturns
      ?.finalSaleRevSharePercentage
      ? String(
          team.settings.returns.finalSaleReturns.finalSaleRevSharePercentage,
        )
      : "0",
    pricingRuleSet: pricingRulesConvertTeamToValue(
      team.settings.returns?.finalSaleReturns?.pricingRuleSet,
      team.settings.countries,
      team.settings.returns?.finalSaleReturns?.percentage,
    ),
    internationalPricingEnabled: team.settings.returns?.finalSaleReturns
      ?.pricingRuleSet?.length
      ? team.settings.returns?.finalSaleReturns?.pricingRuleSet?.length > 1
      : false,
    allRedoPurchasesAreFinalSale:
      team.settings.returns?.finalSaleReturns?.allRedoPurchasesAreFinalSale ??
      false,
    allCovered: team.settings.returns?.finalSaleReturns?.allCovered ?? false,
    allEligible: team.settings.returns?.finalSaleReturns?.allEligible ?? false,
  };
}

export function finalSaleReturnsFormValueToTeam(
  finalSaleReturns: FinalSaleReturnsValue,
) {
  return {
    enabled: finalSaleReturns.enabled,
    validCollections: finalSaleReturns.validCollections,
    validCollectionTitles: finalSaleReturns.validCollectionTitles,
    validProductTags: finalSaleReturns.validProductTags,
    minPrice: Number(finalSaleReturns.minPrice),
    maxPrice: Number(finalSaleReturns.maxPrice),
    percentage: round(+finalSaleReturns.percentage, 3) / 100.0,
    reimbursementPercentage:
      round(+finalSaleReturns.reimbursementPercentage, 3) / 100.0,
    shippingLabelCoveragePrice: finalSaleReturns.shippingLabelCoveragePrice
      ? Number(finalSaleReturns.shippingLabelCoveragePrice)
      : undefined,
    finalSaleRevSharePercentage: Number(
      finalSaleReturns.finalSaleRevSharePercentage,
    ),
    allRedoPurchasesAreFinalSale: finalSaleReturns.allRedoPurchasesAreFinalSale,
    allCovered: finalSaleReturns.allCovered,
    allEligible: finalSaleReturns.allEligible,
    pricingRuleSet: pricingRulesConvertValueToTeam(
      finalSaleReturns.pricingRuleSet,
    ),
  };
}

export const finalSaleReturnsDefault: FinalSaleReturnsValue = {
  enabled: false,
  reimbursementPercentage: "100",
  shippingLabelCoveragePrice: "",
  finalSaleRevSharePercentage: "0",
  allRedoPurchasesAreFinalSale: false,
  allCovered: false,
  allEligible: false,
  validCollections: [] as string[],
  validCollectionTitles: [] as string[],
  validProductTags: [] as string[],
  ...pricingDefault,
};
